import React, { useState, useEffect } from "react";
import {
  Grid,
  Container,
  TextField,
  Typography,
  LinearProgress,
  Box,
  Paper,
  Button,
  Stack,
  Divider,
  Autocomplete,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  FormGroup,
  RadioGroup,
  Radio,
  Menu,
  useMediaQuery,
  InputAdornment,
  Slide,
  ThemeProvider,
  Fade,
  Grow,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useParams } from "react-router-dom";
import { useQuery, gql, useMutation, useLazyQuery } from "@apollo/client";
import { Helmet } from "react-helmet-async";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import countries from "./countries.json";
import { IMAGES_API_URL } from "./constants";
import LogoPng from "./Assets/eventegration.png";
import { getBranding } from "./utils/branding";
// import HowToRegIcon from "@mui/icons-material/HowToReg";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import { DECRYPT_PMT, RegForm } from "./RegistrationPage";
import SocialRegistrationForm from "./Components/SocialRegistrationForm";
import ShareIcon from "@mui/icons-material/Share";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import { useFeatures } from "./Hooks/GetFeatures";
import LinkNotFound from "./Components/LinkNotFound";
import StatusScreen from "./Components/StatusScreen";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { createCustomTheme, parseTheme } from "./utils/themeUtils";
import {
  getGraphQLErrorMessage,
  isGraphQLError,
  analyzeGraphQLError,
} from "./utils/apiUtils";
import LoadingState from "./Components/LoadingState";
import {
  extractReferralParams,
  initializeReferralTracking,
  prepareReferralFormData,
  getReferralLink,
} from "./utils/referralUtils";

// Transition component for dialog animations
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function RegistrationIframe() {
  const { link } = useParams();

  // Extract referral parameters
  const referralParams = extractReferralParams();

  // Get referral link if referral parameters are present
  const registrationLink = referralParams.hasReferralParams
    ? getReferralLink(link, referralParams)
    : link;

  const { data, loading, error } = useQuery(GET_REG_STRUCTURE, {
    variables: { link: registrationLink },
  });
  const searchParams = new URLSearchParams(window.location.search);
  const encryptedPmt = searchParams.get("pmt");

  const { data: pmtData, loading: pmtLoading } = useQuery(DECRYPT_PMT, {
    variables: { encryptedPmt },
    skip: !encryptedPmt,
  });

  const pmt = pmtData?.decryptPmt ? JSON.parse(pmtData.decryptPmt) : null;

  // Theme handling
  const customTheme = createCustomTheme(data?.reg?.theme);
  const parsedTheme = parseTheme(data?.reg?.theme);

  // Social media registration state
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [verifiedEmail, setVerifiedEmail] = useState(""); // Keep for storing social email

  // Social data state for iframe mode
  const [socialData, setSocialData] = useState(null);
  const [mappedData, setMappedData] = useState(null);
  const [missingFields, setMissingFields] = useState([]);

  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const responseBoxRef = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleShareClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const [response, setResponse] = React.useState("");

  // Initialize referral tracking
  React.useEffect(() => {
    initializeReferralTracking(setSnackbar);
  }, []);

  const { featureData, featureLoading } = useFeatures(
    data?.reg?.eventId,
    data?.reg?.role
  );

  const url = encodeURIComponent(window.location.href);
  const title = encodeURIComponent(data?.reg?.eventName || "Event");
  const linkText = "Check it out here";
  const image = `${IMAGES_API_URL}banner/${data?.reg?.eventId}.jpeg`;
  let parsedShareContent = data?.reg?.shareContent
    ? JSON.parse(data?.reg?.shareContent) || null
    : null;
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  console.warn(isSmallScreen);

  let featuresList = [];
  if (featureData && featureData?.getFeatureList) {
    featuresList = JSON.parse(featureData?.getFeatureList.features);
  }

  // Check for social media features and determine available providers (memoized)
  const availableSocialProviders = React.useMemo(() => {
    const providers = [];
    if (featuresList.includes("register-facebook")) {
      providers.push("facebook");
    }
    if (featuresList.includes("register-linkedin")) {
      providers.push("linkedin");
    }
    if (featuresList.includes("register-google")) {
      providers.push("google");
    }
    // console.log("Available social providers calculated:", providers);
    return providers;
  }, [featuresList]);

  // Check if manual registration is enabled
  const isManualEnabled = featuresList.includes("manual-enable");
  const hasSocialFeatures = availableSocialProviders.length > 0;

  // Social media registration state with localStorage persistence
  const getStoredMode = () => {
    try {
      return localStorage.getItem(`reg-mode-${link}`) || "loading";
    } catch {
      return "loading";
    }
  };

  const [registrationMode, setRegistrationMode] = React.useState(getStoredMode);
  const initializedRef = React.useRef(false);
  const userSelectedModeRef = React.useRef(false);

  // Function to set mode and persist it
  const setAndPersistMode = (mode) => {
    // console.log(`Setting registration mode to: ${mode}`);
    setRegistrationMode(mode);
    try {
      localStorage.setItem(`reg-mode-${link}`, mode);
    } catch (e) {
      console.warn("Could not save to localStorage:", e);
    }
  };

  // Update registration mode when features are loaded (only once)
  React.useEffect(() => {
    // console.log("Registration mode useEffect triggered:", {
    //   featureLoading,
    //   hasFeatureData: !!featureData,
    //   availableSocialProvidersLength: availableSocialProviders.length,
    //   isInitialized: initializedRef.current,
    //   userSelectedMode: userSelectedModeRef.current,
    //   currentMode: registrationMode,
    //   storedMode: getStoredMode(),
    // });

    // Check if we have a stored mode that isn't "loading"
    const storedMode = getStoredMode();
    if (storedMode !== "loading") {
      // console.log("Using stored registration mode:", storedMode);
      setRegistrationMode(storedMode);
      initializedRef.current = true;
      userSelectedModeRef.current = true;
      return;
    }

    // Only initialize if features are loaded, not yet initialized, and user hasn't manually selected a mode
    if (
      !featureLoading &&
      featureData &&
      !initializedRef.current &&
      !userSelectedModeRef.current
    ) {
      let newMode;

      if (hasSocialFeatures && isManualEnabled) {
        // Both social and manual available - show choice
        newMode = "choice";
      } else if (hasSocialFeatures && !isManualEnabled) {
        // Only social available - go directly to social choice/form
        newMode = "choice";
      } else if (!hasSocialFeatures && isManualEnabled) {
        // Only manual available - go directly to manual form
        newMode = "manual";
      } else {
        // Neither enabled - default to manual
        newMode = "manual";
      }

      // console.log("Initializing registration mode to:", newMode, {
      //   hasSocialFeatures,
      //   isManualEnabled,
      //   availableSocialProvidersLength: availableSocialProviders.length,
      // });

      setAndPersistMode(newMode);
      initializedRef.current = true;
    }
  }, [
    featureLoading,
    featureData,
    availableSocialProviders,
    hasSocialFeatures,
    isManualEnabled,
  ]);

  // Social media handlers
  const handleSocialChoice = async (provider) => {
    try {
      // console.log(`Social media ${provider} selected (iframe mode)`);
      setSelectedProvider(provider);

      // Skip email verification - go directly to social authentication
      // Start social authentication immediately
      startSocialAuth(provider);

      // console.log("🔍 Social authentication started for:", provider);
    } catch (error) {
      console.error("🚨 Error starting social authentication:", error);
      setSnackbar({
        open: true,
        message: `Failed to start ${provider} authentication: ${error.message}`,
        severity: "error",
      });
    }
  };

  // Start social authentication
  const startSocialAuth = (provider) => {
    // console.log(`Starting social auth for ${provider} (iframe mode)`);

    // Get the current iframe's parent origin for postMessage communication
    const iframeParentOrigin =
      window.location.ancestorOrigins?.length > 0
        ? window.location.ancestorOrigins[0]
        : "*";

    // console.log("🔍 RegistrationIframe - Parent origin:", iframeParentOrigin);
    // console.log("🔍 RegistrationIframe - Event link:", link);

    // Create social auth popup URL - uses our dedicated popup page
    const popupUrl = `${
      window.location.origin
    }/social-auth/popup?provider=${provider}&eventLink=${link}&origin=${encodeURIComponent(
      iframeParentOrigin
    )}`;

    // console.log("🔍 RegistrationIframe - Opening popup:", popupUrl);

    // Open popup window
    const popup = window.open(
      popupUrl,
      "socialAuthIframe",
      "width=600,height=600,scrollbars=yes,resizable=yes"
    );

    if (!popup) {
      console.error(
        "🚨 RegistrationIframe - Failed to open popup (popup blocker?)"
      );
      setSnackbar({
        open: true,
        message:
          "Failed to open authentication window. Please disable popup blocker and try again.",
        severity: "error",
      });
      return;
    }

    // console.log("🔍 RegistrationIframe - Popup opened successfully");

    // Listen for messages from popup
    const messageListener = (event) => {
      // console.log(
      //   "🔍 RegistrationIframe - Received message from:",
      //   event.origin,
      //   event.data
      // );

      // Accept messages from eventegration.com domain (our popup)
      if (!event.origin.includes("eventegration.com")) {
        // console.log(
        //   "🔍 RegistrationIframe - Ignoring message from:",
        //   event.origin
        // );
        return;
      }

      // console.log("🔍 RegistrationIframe - Processing message:", event.data);

      if (event.data.type === "SOCIAL_AUTH_SUCCESS") {
        // console.log(
        //   "🔍 RegistrationIframe - Authentication successful, closing popup"
        // );
        popup.close();
        handleSocialAuthSuccess(event.data);
        window.removeEventListener("message", messageListener);
        clearInterval(checkClosed);
      } else if (event.data.type === "SOCIAL_AUTH_ERROR") {
        // console.log(
        //   "🔍 RegistrationIframe - Authentication error, closing popup"
        // );
        popup.close();
        handleSocialAuthError(event.data.error);
        window.removeEventListener("message", messageListener);
        clearInterval(checkClosed);
      }
    };

    window.addEventListener("message", messageListener);

    // Check if popup is closed
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        // console.log("🔍 RegistrationIframe - Popup was closed by user");
        clearInterval(checkClosed);
        window.removeEventListener("message", messageListener);
        setSnackbar({
          open: true,
          message: "Social authentication was cancelled.",
          severity: "info",
        });
        setRegistrationMode("choice");
      }
    }, 1000);
  };

  // Handle successful social auth
  const handleSocialAuthSuccess = (data) => {
    // console.log("🔍 RegistrationIframe - Social auth successful:", data);
    // console.log(
    //   "🔍 RegistrationIframe - Current selectedProvider:",
    //   selectedProvider
    // );
    // console.log(
    //   "🔍 RegistrationIframe - Provider from popup data:",
    //   data.provider
    // );

    // Store social data for form rendering
    setSocialData(data.socialData);
    setMappedData(data.mappedData);
    setMissingFields(data.missingFields);

    // Set the provider from the received data (in case selectedProvider was reset)
    const providerToUse = data.provider || selectedProvider;
    if (data.provider && data.provider !== selectedProvider) {
      console.log(
        "🔍 RegistrationIframe - Updating selectedProvider to:",
        data.provider
      );
      setSelectedProvider(data.provider);
    }

    // Set verified email from social data
    if (data.socialData?.email) {
      setVerifiedEmail(data.socialData.email);
    }

    // console.log(
    //   "🔍 RegistrationIframe - Setting registration mode to social_form"
    // );
    setRegistrationMode("social_form");

    setSnackbar({
      open: true,
      message: `Successfully authenticated with ${providerToUse}. Please complete your registration.`,
      severity: "success",
    });

    // console.log(
    //   "🔍 RegistrationIframe - Social auth success handling complete. State:",
    //   {
    //     registrationMode: "social_form",
    //     selectedProvider: providerToUse,
    //     hasSocialData: !!data.socialData,
    //     hasMappedData: !!data.mappedData,
    //   }
    // );
  };

  // Handle social auth error
  const handleSocialAuthError = (error) => {
    console.error("Social auth error:", error);
    setSnackbar({
      open: true,
      message: `Authentication failed: ${error}`,
      severity: "error",
    });
    setRegistrationMode("choice");
  };

  // Handle social registration form completion
  const handleSocialRegistrationComplete = async (formData) => {
    try {
      console.log("🔍 RegistrationIframe - Social registration data:", {
        formData,
        socialData,
        mappedData,
        socialEmail: socialData?.email,
      });

      // Prepare data with social email prioritized
      const dataWithSocialEmail = {
        ...mappedData,
        ...formData,
        Email: socialData?.email,
        email: socialData?.email,
      };

      // Add referral data if present
      const finalFormData = prepareReferralFormData(
        dataWithSocialEmail,
        referralParams
      );

      // Use the existing RegForm onSubmit handler
      setResponse(finalFormData);

      console.log("🔍 RegistrationIframe - Social registration completed");
    } catch (error) {
      console.error("🚨 Social registration completion failed:", error);
      setSnackbar({
        open: true,
        message: `Registration failed: ${error.message}`,
        severity: "error",
      });
    }
  };

  const handleManualChoice = () => {
    console.log("Manual choice selected - setting mode to manual");
    setAndPersistMode("manual");
    userSelectedModeRef.current = true; // Mark that user has made a manual selection
    initializedRef.current = true; // Prevent future resets
  };

  const handleBackToChoice = () => {
    // console.log(
    //   "🔍 RegistrationIframe - Back to choice selected, clearing social state"
    // );
    setSelectedProvider(null);
    setVerifiedEmail("");

    // Clear social authentication state
    setSocialData(null);
    setMappedData(null);
    setMissingFields([]);

    setAndPersistMode("choice");
    userSelectedModeRef.current = true; // Mark that user has made a selection
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // console.log(featuresList);
  let description = null;
  let encodedDescription = null;
  let urlRegex = null;
  let foundUrls = null;
  let extractedUrl = null;
  let twitterShareLink = null;
  let linkedinShareLink = null;
  let whatsappShareLink = null;
  let fbShareLink = null;

  if (featuresList.includes("ShareButton")) {
    description = parsedShareContent;
    encodedDescription = encodeURIComponent(description);
    urlRegex = /(https?:\/\/[^\s]+)/g;
    foundUrls = description?.match(urlRegex);

    extractedUrl = foundUrls ? foundUrls[0] : null;

    twitterShareLink = `https://twitter.com/intent/tweet?text=${encodedDescription}`;
    linkedinShareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${extractedUrl}`;
    whatsappShareLink = `https://api.whatsapp.com/send?text=${title} - ${encodedDescription}`;
    fbShareLink = `https://www.facebook.com/sharer/sharer.php?u=${extractedUrl}&quote=${encodedDescription}&picture=${image}`;
  }
  const handleReset = () => {
    setResponse("");
  };

  React.useEffect(() => {
    responseBoxRef?.current?.scrollIntoView();
  }, [response]);

  // Clear stored mode when response is received (form submitted successfully)
  React.useEffect(() => {
    if (response) {
      console.log("Form submitted successfully, clearing stored mode");
      try {
        localStorage.removeItem(`reg-mode-${link}`);
      } catch (e) {
        console.warn("Could not clear localStorage:", e);
      }
    }
  }, [response, link]);

  // Show loading until all required data is fetched and registration mode is determined
  if (
    loading ||
    pmtLoading ||
    featureLoading ||
    registrationMode === "loading"
  ) {
    return (
      <LoadingState
        type="registration"
        eventId={data?.reg?.eventId}
        message="Loading registration options..."
      />
    );
  }

  // Enhanced error handling with GraphQL error analysis
  if (error) {
    console.error("GraphQL Error:", error);
    const errorMessage = getGraphQLErrorMessage(error);
    const errorAnalysis = analyzeGraphQLError(error);

    if (errorAnalysis.isLinkNotFound) {
      return <LinkNotFound />;
    }

    if (errorAnalysis.isPermissionDenied || errorAnalysis.isRegistrationClosed) {
      return (
        <StatusScreen
          variant="warning"
          title="Registration Closed"
          message="This registration form has been deactivated by the event organizer and is no longer accepting entries."
          details="If you believe this is a mistake, please contact the event organizer."
        />
      );
    }

    // Generic error handling
    return (
      <StatusScreen
        variant="error"
        title="Something Went Wrong"
        message={errorMessage}
        actions={[{ label: "Retry", primary: true, onClick: () => window.location.reload() }]}
      />
    );
  }

  if (!data?.reg?.eventId) {
    return <LinkNotFound />;
  }
  if (pmt && pmt.isExhausted) {
    return (
      <StatusScreen
        variant="warning"
        title="Registration Limit Reached"
        message={`${
          pmt.company ? `${pmt.company} has` : "The sponsor has"
        } used up all the registrations allowed on this invitation link.`}
        details="💡 Try contacting the event organizer for alternative registration options."
        eventId={data?.reg?.eventId}
      />
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <Box
        sx={{ backgroundColor: parsedTheme?.background, minHeight: "100vh" }}
      >
        <Container maxWidth="lg" sx={{ py: 4, px: 2 }}>
          {/* A link configured with a custom header image shows it here too. This view never had
              the event banner (the host page carries its own branding) — an explicitly chosen
              image is a different thing: the client asked for it on this exact link. */}
          {data.reg.headerMode === "custom" && data.reg.headerImageUrl && (
            <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
              <img
                src={data.reg.headerImageUrl}
                alt="Event Logo"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
                style={{ maxWidth: "100%", maxHeight: "140px", objectFit: "contain" }}
              />
            </Box>
          )}
          {!response && (
            <Grow in={true} timeout={800}>
              <div
                dangerouslySetInnerHTML={{
                  __html: data.reg.formHeading
                    ? `<h4 style="text-align: center; margin-bottom: 16px; color: ${parsedTheme?.primary};">${data.reg.formHeading}</h4>`
                    : `<h4 style="text-align: center; margin-bottom: 16px; text-transform: capitalize; color: ${parsedTheme?.primary};">${data.reg.role} Registration</h4>`,
                }}
              ></div>
            </Grow>
          )}
          <Zoom in={true} timeout={1000}>
            <Box
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                backgroundColor: parsedTheme?.background,
                borderRadius: 2,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                border: `1px solid ${parsedTheme?.primary}20`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: `linear-gradient(90deg, ${parsedTheme?.primary}, ${parsedTheme?.secondary})`,
                },
              }}
            >
              {response ? (
                <Stack spacing={1} ref={responseBoxRef}>
                  <Typography>
                    <div
                      dangerouslySetInnerHTML={{ __html: response }}
                      style={{ whiteSpace: "pre-line" }}
                    />
                  </Typography>
                  {!featuresList.includes("ShareButton") ? (
                    ""
                  ) : (
                    <Stack
                      direction={"row"}
                      justifyContent={"center"}
                      flexWrap={"wrap"}
                      divider={
                        <Divider
                          orientation={
                            isSmallScreen ? "horizontal" : "vertical"
                          }
                          flexItem
                        />
                      }
                      spacing={2}
                    >
                      <Grid container spacing={2}>
                        <Grid
                          item
                          xs={12}
                          md={3}
                          sx={{ display: "flex", justifyContent: "center" }}
                        >
                          <Button
                            startIcon={<FacebookIcon />}
                            onClick={handleClose}
                            component="a"
                            href={fbShareLink}
                            variant="contained"
                            target="_blank"
                            size="large"
                            rel="noopener noreferrer"
                            sx={{
                              backgroundColor: "#1877F2",
                              color: "white",
                              "&:hover": { backgroundColor: "#145db2" },
                            }}
                          >
                            Share on Facebook
                          </Button>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={3}
                          sx={{ display: "flex", justifyContent: "center" }}
                        >
                          <Button
                            startIcon={<TwitterIcon />}
                            onClick={handleClose}
                            component="a"
                            href={twitterShareLink}
                            target="_blank"
                            size="large"
                            rel="noopener noreferrer"
                            sx={{
                              backgroundColor: "#1DA1F2",
                              color: "white",
                              "&:hover": { backgroundColor: "#1a8acb" },
                            }}
                          >
                            Share on Twitter
                          </Button>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={3}
                          sx={{ display: "flex", justifyContent: "center" }}
                        >
                          <Button
                            startIcon={<LinkedInIcon />}
                            onClick={handleClose}
                            component="a"
                            href={linkedinShareLink}
                            target="_blank"
                            size="large"
                            rel="noopener noreferrer"
                            sx={{
                              backgroundColor: "#0A66C2",
                              color: "white",
                              "&:hover": { backgroundColor: "#084a82" },
                            }}
                          >
                            Share on LinkedIn
                          </Button>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={3}
                          sx={{ display: "flex", justifyContent: "center" }}
                        >
                          <Button
                            startIcon={<WhatsAppIcon />}
                            onClick={handleClose}
                            component="a"
                            href={whatsappShareLink}
                            target="_blank"
                            size="large"
                            rel="noopener noreferrer"
                            sx={{
                              backgroundColor: "#25D366",
                              color: "white",
                              "&:hover": { backgroundColor: "#1ca851" },
                            }}
                          >
                            Share on WhatsApp
                          </Button>
                        </Grid>
                      </Grid>
                    </Stack>
                  )}
                </Stack>
              ) : registrationMode === "social_form" ? (
                // Social Form with Email Pre-filled
                (() => {
                  console.log(
                    "🔍 RegistrationIframe - Rendering social form with state:",
                    {
                      registrationMode,
                      selectedProvider,
                      hasSocialData: !!socialData,
                      hasMappedData: !!mappedData,
                      socialDataProvider: socialData?.provider,
                    }
                  );

                  // Use provider from social data as fallback
                  const formProvider =
                    selectedProvider || socialData?.provider || "social";

                  return (
                    <Box>
                      <Box sx={{ mb: 2 }}>
                        <Button
                          onClick={handleBackToChoice}
                          startIcon={
                            <ShareIcon sx={{ transform: "scaleX(-1)" }} />
                          }
                          variant="text"
                          size="small"
                          sx={{
                            color: parsedTheme?.primary,
                            textTransform: "none",
                            "&:hover": {
                              backgroundColor: `${parsedTheme?.primary}10`,
                            },
                          }}
                        >
                          Back to registration options
                        </Button>
                      </Box>
                      <SocialRegistrationForm
                        socialData={socialData}
                        mappedData={mappedData}
                        missingFields={missingFields}
                        allFields={data.reg.fields}
                        onSubmit={handleSocialRegistrationComplete}
                        provider={formProvider}
                        loading={false}
                        eventId={data.reg.eventId}
                      />
                    </Box>
                  );
                })()
              ) : registrationMode === "choice" &&
                availableSocialProviders.length > 0 ? (
                // Enhanced Social Media Registration UI
                <Fade in={true} timeout={800}>
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{
                        color: parsedTheme?.primary,
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      🚀 Quick Registration
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        mb: 4,
                        maxWidth: 400,
                        mx: "auto",
                      }}
                    >
                      Register instantly using your social media account or fill
                      out the form manually.
                    </Typography>

                    <Stack spacing={3} sx={{ maxWidth: 420, mx: "auto" }}>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: parsedTheme?.primary,
                            mb: 2,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                          }}
                        >
                          Social Media Login
                        </Typography>
                        <Stack spacing={2}>
                          {availableSocialProviders.map((provider) => {
                            const providerConfig = {
                              facebook: {
                                icon: <FacebookIcon sx={{ fontSize: 20 }} />,
                                color: "#1877F2",
                                hoverColor: "#145db2",
                                label: "Continue with Facebook",
                              },
                              linkedin: {
                                icon: <LinkedInIcon sx={{ fontSize: 20 }} />,
                                color: "#0A66C2",
                                hoverColor: "#084a82",
                                label: "Continue with LinkedIn",
                              },
                              google: {
                                icon: (
                                  <Box
                                    sx={{
                                      fontSize: 18,
                                      fontWeight: 700,
                                      fontFamily: "Arial, sans-serif",
                                      color: "white",
                                    }}
                                  >
                                    G
                                  </Box>
                                ),
                                color: "#EA4335",
                                hoverColor: "#d32f2f",
                                label: "Continue with Google",
                              },
                            };

                            const config = providerConfig[provider];

                            return (
                              <Button
                                key={provider}
                                startIcon={config.icon}
                                onClick={() => handleSocialChoice(provider)}
                                variant="contained"
                                size="large"
                                fullWidth
                                sx={{
                                  backgroundColor: config.color,
                                  color: "white",
                                  py: 1.5,
                                  borderRadius: 2,
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                  textTransform: "none",
                                  fontSize: "1rem",
                                  fontWeight: 500,
                                  "&:hover": {
                                    backgroundColor: config.hoverColor,
                                    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                                    transform: "translateY(-1px)",
                                  },
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                {config.label}
                              </Button>
                            );
                          })}
                        </Stack>
                      </Box>

                      {/* Manual Registration Section - Only show if manual-enable feature is enabled */}
                      {isManualEnabled && (
                        <>
                          <Divider
                            sx={{
                              my: 3,
                              "&::before, &::after": {
                                borderColor: parsedTheme?.primary + "40",
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                px: 2,
                                fontWeight: 500,
                              }}
                            >
                              OR
                            </Typography>
                          </Divider>

                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: parsedTheme?.primary,
                                mb: 2,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: 1,
                              }}
                            >
                              Manual Registration
                            </Typography>
                            <Button
                              onClick={handleManualChoice}
                              variant="outlined"
                              size="large"
                              fullWidth
                              startIcon={<AppRegistrationIcon />}
                              sx={{
                                borderColor: parsedTheme?.primary,
                                color: parsedTheme?.primary,
                                py: 1.5,
                                borderRadius: 2,
                                borderWidth: 2,
                                textTransform: "none",
                                fontSize: "1rem",
                                fontWeight: 500,
                                "&:hover": {
                                  borderColor: parsedTheme?.primary,
                                  backgroundColor: `${parsedTheme?.primary}10`,
                                  borderWidth: 2,
                                  transform: "translateY(-1px)",
                                },
                                transition: "all 0.2s ease-in-out",
                              }}
                            >
                              Fill Registration Form
                            </Button>
                          </Box>
                        </>
                      )}
                    </Stack>
                  </Box>
                </Fade>
              ) : (
                // Manual Registration Form with Back Button
                <Box>
                  {availableSocialProviders.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Button
                        onClick={handleBackToChoice}
                        startIcon={
                          <ShareIcon sx={{ transform: "scaleX(-1)" }} />
                        }
                        variant="text"
                        size="small"
                        sx={{
                          color: parsedTheme?.primary,
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: `${parsedTheme?.primary}10`,
                          },
                        }}
                      >
                        Back to registration options
                      </Button>
                    </Box>
                  )}
                  <RegForm
                    fields={data.reg.fields}
                    onSubmit={(res) => {
                      // Add referral data if present
                      const finalFormData = prepareReferralFormData(
                        res,
                        referralParams
                      );
                      setResponse(finalFormData);
                    }}
                    eventId={data.reg.eventId}
                    role={data.reg.role}
                    featuresList={featuresList}
                    pmt={pmt || null}
                    customTheme={parsedTheme}
                    referralParams={referralParams}
                    isMobile={isSmallScreen}
                  />
                </Box>
              )}
            </Box>
          </Zoom>
          <Fade in={true} timeout={1500}>
            <Box
              sx={{
                py: 6,
                display: "flex",
                justifyContent: { xs: "center", md: "right" },
              }}
            >
              {(() => {
                const branding = getBranding(data?.reg?.eventId);
                return (
                  <Box>
                    <Typography color="text.secondary" fontSize={12}>
                      Powered by {branding.companyName}
                    </Typography>
                    <a href={branding.websiteUrl}>
                      <img
                        src={branding.logoUrl || LogoPng}
                        width={180}
                        alt={`${branding.companyName} Logo`}
                      />
                    </a>
                  </Box>
                );
              })()}
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

const GET_REG_STRUCTURE = gql`
  query ($link: String!) {
    reg(link: $link) {
      eventId
      eventName
      role
      formHeading
      headerMode
      headerImageUrl
      shareContent
      theme
      fields {
        name
        type
        label
        sx
        xs
        md
        placeholder
        options
        defaultValue
        minLength
        maxLength
        pattern
        required
        readonly
      }
    }
  }
`;
